import { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { calculateDurationMinutes, formatDuration } from '../../utils/scheduleUtils';

// Days of the week
const DAYS = [
  { key: 'mon', label: 'Mon', fullLabel: 'Monday' },
  { key: 'tue', label: 'Tue', fullLabel: 'Tuesday' },
  { key: 'wed', label: 'Wed', fullLabel: 'Wednesday' },
  { key: 'thu', label: 'Thu', fullLabel: 'Thursday' },
  { key: 'fri', label: 'Fri', fullLabel: 'Friday' },
  { key: 'sat', label: 'Sat', fullLabel: 'Saturday' },
  { key: 'sun', label: 'Sun', fullLabel: 'Sunday' }
];

// Pattern types
const PATTERN_TYPES = [
  { value: 'fixed', label: 'Fixed', description: 'Same time and duration every day' },
  { value: 'custom', label: 'Custom', description: 'Different times and duration per day' }
];

// Default start time only - end time must be set by user
const DEFAULT_START_TIME = '09:00';

export default function RecurringScheduleConfig({
  value, // New format: { pattern, days, startTime, endTime, customSchedule: { mon: { startTime, endTime } } }
  onChange,
  endDate,
  onEndDateChange,
  className,
  disabled = false
}) {
  const [pattern, setPattern] = useState(value?.pattern || 'fixed');
  const [selectedDays, setSelectedDays] = useState(value?.days || []);

  // Fixed pattern: same start/end time for all days
  const [fixedStartTime, setFixedStartTime] = useState(
    value?.startTime || value?.time || DEFAULT_START_TIME
  );
  const [fixedEndTime, setFixedEndTime] = useState(
    value?.endTime || '' // No default - user must set end time
  );

  // Custom pattern: different start/end times per day
  const [customSchedule, setCustomSchedule] = useState(() => {
    // Initialize from new format or convert from old format
    if (value?.customSchedule) {
      return value.customSchedule;
    }
    // Convert old customTimes format to new customSchedule format
    if (value?.customTimes) {
      const converted = {};
      Object.entries(value.customTimes).forEach(([day, time]) => {
        converted[day] = { startTime: time, endTime: '' }; // No default end time
      });
      return converted;
    }
    return {};
  });

  const [hasEndDate, setHasEndDate] = useState(!!endDate);

  // Track if this is initial mount
  const isInitialMount = useRef(true);

  // Calculate duration for fixed pattern
  const fixedDuration = calculateDurationMinutes(fixedStartTime, fixedEndTime);

  // Emit changes - only after initial mount
  const emitChange = useCallback((newPattern, newDays, newStartTime, newEndTime, newCustomSchedule) => {
    const scheduleData = {
      pattern: newPattern,
      days: newDays,
      frequency: 'weekly'
    };

    if (newPattern === 'fixed') {
      scheduleData.startTime = newStartTime;
      scheduleData.endTime = newEndTime;
    } else {
      scheduleData.customSchedule = newCustomSchedule;
    }

    onChange?.(scheduleData);
  }, [onChange]);

  // Skip emitting on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    emitChange(pattern, selectedDays, fixedStartTime, fixedEndTime, customSchedule);
  }, [pattern, selectedDays, fixedStartTime, fixedEndTime, customSchedule]);

  // When switching TO custom pattern, initialize customSchedule for all selected days
  useEffect(() => {
    if (pattern === 'custom' && selectedDays?.length > 0) {
      const needsInit = selectedDays.some(dayKey => !customSchedule[dayKey]);
      if (needsInit) {
        setCustomSchedule(prev => {
          const updated = { ...prev };
          selectedDays.forEach(dayKey => {
            if (!updated[dayKey]) {
              updated[dayKey] = {
                startTime: DEFAULT_START_TIME,
                endTime: '' // No default - user must set
              };
            }
          });
          return updated;
        });
      }
    }
  }, [pattern, selectedDays]);

  // Handle end date toggle
  useEffect(() => {
    if (!hasEndDate) {
      onEndDateChange?.(null);
    }
  }, [hasEndDate]);

  // Toggle day selection
  const toggleDay = (dayKey) => {
    if (disabled) return;

    setSelectedDays(prev => {
      if (prev?.includes(dayKey)) {
        // Remove day
        const newDays = prev?.filter(d => d !== dayKey);
        // Also remove from custom schedule
        if (pattern === 'custom') {
          setCustomSchedule(cs => {
            const newCs = { ...cs };
            delete newCs[dayKey];
            return newCs;
          });
        }
        return newDays;
      } else {
        // Add day
        if (pattern === 'custom') {
          setCustomSchedule(cs => ({
            ...cs,
            [dayKey]: { startTime: DEFAULT_START_TIME, endTime: '' } // No default end time
          }));
        }
        return [...prev, dayKey];
      }
    });
  };

  // Update custom schedule for a day
  const updateCustomSchedule = (dayKey, field, value) => {
    setCustomSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value
      }
    }));
  };

  // Get sorted selected days
  const sortedSelectedDays = DAYS?.filter(d => selectedDays?.includes(d?.key));

  return (
    <div className={cn('space-y-4', className)}>
      {/* Pattern Selection */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-2">
          Schedule Pattern
        </label>
        <div className="flex gap-4">
          {PATTERN_TYPES?.map(pt => (
            <label
              key={pt?.value}
              className={cn(
                'flex-1 p-3 rounded-lg border cursor-pointer transition-colors',
                pattern === pt?.value
                  ? 'border-accent bg-accent/10'
                  : 'border-charcoal-shade-2 bg-surface-charcoal hover:border-charcoal-shade-1',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name="pattern"
                value={pt?.value}
                checked={pattern === pt?.value}
                onChange={(e) => setPattern(e?.target?.value)}
                disabled={disabled}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                  pattern === pt?.value ? 'border-accent' : 'border-charcoal-shade-2'
                )}>
                  {pattern === pt?.value && (
                    <div className="w-2 h-2 rounded-full bg-accent" />
                  )}
                </div>
                <div>
                  <p className={cn('text-sm font-medium', pattern === pt?.value ? 'text-accent' : 'text-text-primary')}>{pt?.label}</p>
                  <p className="text-xs text-white-shade-2">{pt?.description}</p>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Day Selection */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-2">
          Select Days
        </label>
        <div className="flex flex-wrap gap-2">
          {DAYS?.map(day => (
            <button
              key={day?.key}
              type="button"
              onClick={() => toggleDay(day?.key)}
              disabled={disabled}
              className={cn(
                'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                selectedDays?.includes(day?.key)
                  ? 'bg-accent text-white'
                  : 'bg-surface-charcoal text-white-shade-1 hover:bg-charcoal-shade-1 border border-charcoal-shade-2',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {day?.label}
            </button>
          ))}
        </div>
        {selectedDays?.length === 0 && (
          <p className="mt-2 text-xs text-yellow-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Select at least one day
          </p>
        )}
      </div>

      {/* Time Configuration */}
      {selectedDays?.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-white-shade-1 mb-2">
            {pattern === 'fixed' ? 'Time Range (same for all days)' : 'Time Range per Day'}
          </label>

          {pattern === 'fixed' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-text-secondary" />
                  <span className="text-sm text-text-secondary">Start:</span>
                  <input
                    type="time"
                    value={fixedStartTime}
                    onChange={(e) => setFixedStartTime(e?.target?.value)}
                    disabled={disabled}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-background focus:outline-none focus:border-primary-orange"
                  />
                </div>
                <span className="text-text-secondary">to</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-secondary">End:</span>
                  <input
                    type="time"
                    value={fixedEndTime}
                    onChange={(e) => setFixedEndTime(e?.target?.value)}
                    disabled={disabled}
                    className={cn(
                      'px-3 py-2 bg-white border rounded-lg text-background focus:outline-none focus:border-primary-orange',
                      !fixedEndTime ? 'border-yellow-500' : 'border-gray-300'
                    )}
                  />
                </div>
                {fixedDuration ? (
                  <span className="text-sm font-medium text-accent bg-accent/10 px-2 py-1 rounded">
                    {formatDuration(fixedDuration)}
                  </span>
                ) : (
                  <span className="text-sm text-yellow-500">Set end time</span>
                )}
              </div>
              {!fixedEndTime && (
                <p className="text-xs text-yellow-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  End time is required
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {sortedSelectedDays?.map(day => {
                const schedule = customSchedule?.[day?.key] || { startTime: DEFAULT_START_TIME, endTime: '' };
                const duration = calculateDurationMinutes(schedule.startTime, schedule.endTime);
                const missingEndTime = !schedule.endTime;

                return (
                  <div key={day?.key} className={cn(
                    'flex items-center gap-3 p-3 bg-surface-charcoal rounded-lg border',
                    missingEndTime ? 'border-yellow-500' : 'border-charcoal-shade-2'
                  )}>
                    <span className="w-24 text-sm font-medium text-text-primary">{day?.fullLabel}</span>
                    <input
                      type="time"
                      value={schedule.startTime}
                      onChange={(e) => updateCustomSchedule(day?.key, 'startTime', e?.target?.value)}
                      disabled={disabled}
                      className="px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-background text-sm focus:outline-none focus:border-primary-orange"
                    />
                    <span className="text-text-secondary text-sm">to</span>
                    <input
                      type="time"
                      value={schedule.endTime}
                      onChange={(e) => updateCustomSchedule(day?.key, 'endTime', e?.target?.value)}
                      disabled={disabled}
                      className={cn(
                        'px-2 py-1.5 bg-white border rounded-lg text-background text-sm focus:outline-none focus:border-primary-orange',
                        missingEndTime ? 'border-yellow-500' : 'border-gray-300'
                      )}
                    />
                    {duration ? (
                      <span className="text-sm font-medium text-accent ml-auto">
                        {formatDuration(duration)}
                      </span>
                    ) : (
                      <span className="text-sm text-yellow-500 ml-auto">Set end time</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* End Date */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-2">
          End Date
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!hasEndDate}
              onChange={(e) => setHasEndDate(!e?.target?.checked)}
              disabled={disabled}
              className="w-4 h-4 rounded border-gray-300 bg-white text-primary-orange focus:ring-primary-orange focus:ring-offset-dark-charcoal"
            />
            <span className="text-sm text-text-primary">No end date (runs until manually stopped)</span>
          </label>

          {hasEndDate && (
            <div className="flex items-center gap-2 ml-6">
              <Calendar className="w-4 h-4 text-text-secondary" />
              <input
                type="date"
                value={endDate || ''}
                onChange={(e) => onEndDateChange?.(e?.target?.value)}
                disabled={disabled}
                min={new Date().toISOString().split('T')[0]}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-background focus:outline-none focus:border-primary-orange"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      {selectedDays?.length > 0 && (
        <div className="p-3 bg-dark-charcoal rounded-lg border border-charcoal-shade-2">
          <p className="text-xs text-white-shade-2 mb-1">Schedule Summary</p>
          <p className="text-sm text-text-primary">
            {pattern === 'fixed' ? (
              <>
                Every <span className="text-primary-orange">{sortedSelectedDays?.map(d => d?.label)?.join(', ')}</span>
                {' '}from <span className="text-primary-orange">{fixedStartTime}</span>
                {' '}to <span className="text-primary-orange">{fixedEndTime}</span>
                {fixedDuration && (
                  <span className="text-accent"> ({formatDuration(fixedDuration)})</span>
                )}
              </>
            ) : (
              <>
                {sortedSelectedDays?.map((d, i) => {
                  const schedule = customSchedule?.[d?.key] || {};
                  const duration = calculateDurationMinutes(schedule.startTime, schedule.endTime);
                  return (
                    <span key={d?.key}>
                      {i > 0 && ', '}
                      <span className="text-primary-orange">{d?.label}</span>
                      {' '}{schedule.startTime}-{schedule.endTime}
                      {duration && <span className="text-accent"> ({formatDuration(duration)})</span>}
                    </span>
                  );
                })}
              </>
            )}
            {hasEndDate && endDate && (
              <> until <span className="text-primary-orange">{new Date(endDate).toLocaleDateString('en-AU')}</span></>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
